import { useMutation, useQuery } from "@apollo/client";
import Clear from "@mui/icons-material/Clear";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  EPISODIC_CANDIDATE_SEARCH_LIMIT,
  EPISODIC_CANDIDATE_SEARCH_MIN_LENGTH,
  episodicCandidateSearchTooShort,
} from "encamp-shared/src/waste/episodicCandidateSearch";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../Containers/containerDisplay";
import {
  EPISODIC_MANIFEST_CANDIDATES_QUERY,
  SET_EPISODIC_MANIFEST_EXEMPTIONS_MUTATION,
} from "./api";
import { episodicWindowEnd, formatPounds } from "./episodicDisplay";
import { describeAttachError } from "./episodicErrors";
import {
  canSaveDrafts,
  draftError,
  draftsForManifest,
  draftsToInputs,
  EventManifestExemptions,
  isLockedDraft,
  MANIFEST_REFETCH_QUERIES,
  ManifestCandidate,
  runningTotal,
  StreamAmountDraft,
} from "./manifestExemptionForm";

const STEPS = ["Choose a manifest", "Set the episodic amount"];

const SEARCH_DELAY_MS = 300;

/** Two fixed lines per manifest: identity, then streams and fit. Per-stream
 * detail waits for the amount step, so rows stay the same height however many
 * streams a manifest carries. */
function CandidateCard({
  candidate,
  onThisEvent,
  showCodes,
}: {
  candidate: ManifestCandidate;
  onThisEvent: boolean;
  showCodes: boolean;
}) {
  const streamCount = candidate.streams.length;
  const matchingCodes = [...new Set(candidate.streams.flatMap((s) => s.matchingWasteCodes))].sort();
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600}>
          {candidate.manifestTrackingNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Shipped {formatContainerDate(candidate.shippedDate)}
        </Typography>
        {onThisEvent && <Chip size="small" label="On this event" />}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          {streamCount === 1 ? "1 waste stream" : `${streamCount} waste streams`}
        </Typography>
        {showCodes &&
          matchingCodes.map((code) => (
            <Tooltip key={code} title="Also on the event's Form 8700-12">
              <Chip size="small" variant="outlined" color="info" label={code} />
            </Tooltip>
          ))}
        {showCodes && matchingCodes.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No waste codes in common with this event
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/** The manifest finder: a search box over the radio list of candidates. With
 * nothing typed the list is the server's suggestions for the event's window,
 * ranked by waste-code overlap and nearness to the start date. Typing filters
 * by tracking number; picking a row fills the box with that number so the list
 * collapses to it; clearing the box brings the suggestions back. */
function ChooseManifestStep({
  candidates,
  loading,
  search,
  onSearch,
  selected,
  onSelect,
  startDate,
  manifestsOnEvent,
  showCodes,
}: {
  candidates: ManifestCandidate[];
  loading: boolean;
  search: string;
  onSearch: (value: string) => void;
  selected: string | null;
  onSelect: (manifestTrackingNumber: string | null) => void;
  startDate: string;
  manifestsOnEvent: Set<string>;
  showCodes: boolean;
}) {
  const searchTooShort = episodicCandidateSearchTooShort(search);
  // The server cuts a search at this many manifests, so a full page means more may match.
  const searchTruncated =
    search.trim() !== "" && candidates.length >= EPISODIC_CANDIDATE_SEARCH_LIMIT;
  const windowEnd = episodicWindowEnd(startDate);
  const windowCopy = windowEnd
    ? `Only manifests shipped ${formatContainerDate(startDate)} through ${formatContainerDate(windowEnd)} qualify.`
    : "";
  // The list offers what the server would accept: a manifest with any stream
  // shipped inside the window. Streams outside it are locked on the amount step.
  const offered = candidates.filter((candidate) => candidate.inSuggestionWindow);
  const pick = (manifestTrackingNumber: string | null) => {
    onSelect(manifestTrackingNumber);
    onSearch(manifestTrackingNumber ?? "");
  };
  return (
    <Stack spacing={2.5}>
      <TextField
        fullWidth
        value={search}
        placeholder="Find a manifest by tracking number"
        onChange={(event) => {
          onSelect(null);
          onSearch(event.target.value);
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton aria-label="Clear" size="small" onClick={() => pick(null)}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">
          Manifests that may carry waste from this event ({offered.length} found)
        </Typography>
        {showCodes && (
          <Typography variant="body2" color="text.secondary">
            Codes shown are the ones each manifest shares with the event's Form 8700-12 filing.
          </Typography>
        )}
      </Stack>
      {searchTooShort && (
        <Typography variant="body2" color="text.secondary">
          Type at least {EPISODIC_CANDIDATE_SEARCH_MIN_LENGTH} characters of the tracking number.
        </Typography>
      )}
      {!loading && !searchTooShort && offered.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No manifests found. {windowCopy}
        </Typography>
      )}
      {searchTruncated && (
        <Typography variant="body2" color="text.secondary">
          Showing the first {EPISODIC_CANDIDATE_SEARCH_LIMIT} matches. Type more of the number to
          narrow the list.
        </Typography>
      )}
      <RadioGroup
        value={selected ?? ""}
        onChange={(_e, value) => pick(value)}
        data-testid="manifest-candidates"
        // Every candidate loads, so a busy window scrolls inside the dialog
        // instead of growing it past the viewport.
        sx={{ maxHeight: "45vh", overflowY: "auto", pr: 1 }}
      >
        <Stack spacing={2.5}>
          {offered.map((candidate) => {
            return (
              <FormControlLabel
                key={candidate.manifestTrackingNumber}
                value={candidate.manifestTrackingNumber}
                control={<Radio size="small" sx={{ p: 0, mr: 1 }} />}
                // Top-aligned so the radio sits on the first line of a multi-line
                // option; a padless small radio is exactly one body2 line tall.
                sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                label={
                  <CandidateCard
                    candidate={candidate}
                    onThisEvent={manifestsOnEvent.has(candidate.manifestTrackingNumber)}
                    showCodes={showCodes}
                  />
                }
              />
            );
          })}
        </Stack>
      </RadioGroup>
    </Stack>
  );
}

function StreamAmountRow({
  draft,
  showCodes,
  onChange,
}: {
  draft: StreamAmountDraft;
  /** Code chips and the no-overlap caption mean nothing until the event has an EPA record to compare against. */
  showCodes: boolean;
  onChange: (next: StreamAmountDraft) => void;
}) {
  const error = draftError(draft);
  const noOverlap = showCodes && draft.matchingWasteCodes.length === 0;
  const locked = isLockedDraft(draft);
  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {draft.name}
          </Typography>
          {showCodes &&
            draft.matchingWasteCodes.map((code) => (
              <Chip key={code} size="small" variant="outlined" color="info" label={code} />
            ))}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          of {formatPounds(draft.groupPounds)} on this manifest
        </Typography>
        {!locked && (
          <Button
            size="small"
            disabled={safeParseFloat(draft.pounds) === draft.capPounds}
            onClick={() => onChange({ ...draft, pounds: String(draft.capPounds) })}
          >
            Whole amount
          </Button>
        )}
        <TextField
          size="small"
          type="number"
          label="lb"
          value={draft.pounds}
          disabled={locked}
          error={error !== null}
          onChange={(e) => onChange({ ...draft, pounds: e.target.value })}
          onFocus={(e) => e.target.select()}
          slotProps={{ htmlInput: { min: 0, step: "any", "aria-label": `${draft.name} pounds` } }}
          sx={{ width: 110 }}
        />
      </Stack>
      {!draft.inWindow && (
        <Typography variant="caption" color="text.secondary">
          Shipped {formatContainerDate(draft.firstShipped)}, outside the event's 60-day window, so
          it cannot be declared.
        </Typography>
      )}
      {draft.inWindow && draft.capPounds <= 0 && (
        <Typography variant="caption" color="text.secondary">
          Other events already hold all of {draft.name} on this manifest.
        </Typography>
      )}
      {locked && draft.declaredPounds !== null && (
        <Typography variant="caption" color="warning.main">
          Saving removes the {formatPounds(draft.declaredPounds)} this event holds here.
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      {noOverlap && (
        <Typography variant="caption" color="text.secondary">
          No waste codes in common with this event
        </Typography>
      )}
    </Stack>
  );
}

/** Where the amount step can send the user to see the event itself, when the
 * dialog was opened from the manifest side rather than from the event page. */
export type EventLink = { href: string; reason: string };

function SetAmountsStep({
  candidate,
  drafts,
  showCodes,
  eventLink,
  onChange,
}: {
  candidate: ManifestCandidate;
  eventLink?: EventLink;
  drafts: StreamAmountDraft[];
  showCodes: boolean;
  onChange: (drafts: StreamAmountDraft[]) => void;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">
        {candidate.manifestTrackingNumber} · Shipped {formatContainerDate(candidate.shippedDate)}
      </Typography>
      {eventLink && (
        <Typography variant="body2">
          Declared on{" "}
          <Link component={RouterLink} to={eventLink.href}>
            {eventLink.reason}
          </Link>
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        How much of each waste stream on this manifest came from this event? Those pounds are left
        out of the calculated generator status for the month they shipped. The manifest still
        appears on your biennial report.
      </Typography>
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
        {drafts.map((draft, index) => (
          <StreamAmountRow
            key={draft.wasteStreamId}
            draft={draft}
            showCodes={showCodes}
            onChange={(next) => onChange(drafts.map((d, i) => (i === index ? next : d)))}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function RunningTotal({
  exemptions,
  before,
  after,
}: {
  exemptions: EventManifestExemptions;
  before: number;
  after: number;
}) {
  const estimate =
    exemptions.epaEstimatedPounds != null
      ? ` of ${formatPounds(exemptions.epaEstimatedPounds)} estimated on Form 8700-12`
      : " excluded from generator status";
  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <Typography variant="body2">
        <Box component="span" fontWeight={600}>
          {formatPounds(before)}
        </Box>
        {after !== before && (
          <>
            {" → "}
            <Box component="span" fontWeight={600}>
              {formatPounds(after)}
            </Box>
          </>
        )}
        {estimate}
      </Typography>
      {exemptions.filedWithEpa && (
        <Chip size="small" color="success" variant="outlined" label="Filed with EPA" />
      )}
    </Stack>
  );
}

/** The second step: the relief note, then the per-stream amounts for the chosen manifest. */
function AmountStep({
  reliefApplies,
  candidate,
  loading,
  showCodes,
  eventLink,
  drafts,
  onChange,
}: {
  reliefApplies: boolean;
  candidate: ManifestCandidate | null;
  loading: boolean;
  showCodes: boolean;
  eventLink?: EventLink;
  drafts: StreamAmountDraft[];
  onChange: (next: StreamAmountDraft[]) => void;
}) {
  return (
    <>
      {!reliefApplies && (
        <Alert severity="info">
          This state has not adopted the episodic rule. The pounds are recorded on the event but
          keep counting toward generator status.
        </Alert>
      )}
      {candidate ? (
        <SetAmountsStep
          showCodes={showCodes}
          eventLink={eventLink}
          candidate={candidate}
          drafts={drafts}
          onChange={onChange}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {loading ? "Loading the manifest…" : "This manifest could not be found."}
        </Typography>
      )}
    </>
  );
}

export function AddEventManifestDialog({
  eventId,
  startDate,
  exemptions,
  initialManifestTrackingNumber = null,
  eventLink,
  reliefApplies = true,
  title,
  onBack,
  extraRefetchQueries = [],
  open,
  onClose,
}: {
  eventId: string;
  startDate: string;
  exemptions: EventManifestExemptions;
  initialManifestTrackingNumber?: string | null;
  /** Set when opened from a manifest, so the amount step can link to the event. */
  eventLink?: EventLink;
  /** False where subpart L relief does not reach the facility, so declared pounds exempt nothing. */
  reliefApplies?: boolean;
  title?: string;
  onBack?: () => void;
  extraRefetchQueries?: string[];
  open: boolean;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const editing = initialManifestTrackingNumber !== null;
  const [search, setSearch] = useState(initialManifestTrackingNumber ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selected, setSelected] = useState<string | null>(initialManifestTrackingNumber);
  const [step, setStep] = useState(editing ? 1 : 0);
  const [edited, setEdited] = useState<{ manifest: string; drafts: StreamAmountDraft[] } | null>(
    null
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, previousData, loading } = useQuery(EPISODIC_MANIFEST_CANDIDATES_QUERY, {
    variables: { id: eventId, search: debouncedSearch.trim() === "" ? null : debouncedSearch },
    skip: !open || episodicCandidateSearchTooShort(debouncedSearch),
    fetchPolicy: "cache-and-network",
  });
  // A pick refetches the list scoped to its number; the previous list stands in
  // while that request is in flight so the amount step never loses its candidate.
  // A search the server would answer empty lists nothing while it is typed.
  const searchTooShort = episodicCandidateSearchTooShort(search);
  const candidates = useMemo(
    () =>
      searchTooShort
        ? []
        : (data?.episodicEvent?.manifestCandidates ??
          previousData?.episodicEvent?.manifestCandidates ??
          []),
    [searchTooShort, data, previousData]
  );
  const candidate = candidates.find((c) => c.manifestTrackingNumber === selected) ?? null;
  const existing = exemptions.manifests.find((m) => m.manifestTrackingNumber === selected) ?? null;
  const manifestsOnEvent = useMemo(
    () => new Set(exemptions.manifests.map((m) => m.manifestTrackingNumber)),
    [exemptions]
  );

  const drafts = useMemo(() => {
    if (edited && edited.manifest === selected) {
      return edited.drafts;
    }
    return candidate ? draftsForManifest(candidate, existing, startDate) : [];
  }, [edited, selected, candidate, existing, startDate]);
  const setDrafts = (next: StreamAmountDraft[]) => {
    if (selected) {
      setEdited({ manifest: selected, drafts: next });
    }
  };

  const [setExemptions, { loading: saving }] = useMutation(
    SET_EPISODIC_MANIFEST_EXEMPTIONS_MUTATION,
    { refetchQueries: [...MANIFEST_REFETCH_QUERIES, ...extraRefetchQueries] }
  );

  const handleSave = async () => {
    if (!candidate) {
      return;
    }
    await setExemptions({
      variables: {
        episodicEventId: eventId,
        manifestTrackingNumber: candidate.manifestTrackingNumber,
        streams: draftsToInputs(drafts),
      },
      onCompleted: () => {
        alerts.success(`${candidate.manifestTrackingNumber} saved to this event`);
        onClose();
      },
      onError: (error) => {
        alerts.error(describeAttachError(error, "The manifest could not be saved"));
      },
    });
  };

  const totals = runningTotal(exemptions.totalExemptPounds, existing, drafts, reliefApplies);
  const onAmountStep = step === 1;
  const back = editing ? onBack : () => setStep(0);
  const primary = onAmountStep
    ? {
        saveText: "Save",
        onClick: handleSave,
        loading: saving,
        disabled: candidate === null || !canSaveDrafts(drafts),
      }
    : {
        saveText: "Next",
        onClick: () => setStep(1),
        disabled: candidate === null || !candidate.inSuggestionWindow,
      };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={title ?? (editing ? "Edit episodic pounds" : "Add a manifest to this event")}
      primaryActionButtonProps={primary}
      additionalPrimaryActionElements={
        onAmountStep && back
          ? [
              <Button key="back" onClick={back} disabled={saving}>
                Back
              </Button>,
            ]
          : []
      }
      secondaryActionElements={[
        <RunningTotal
          key="total"
          exemptions={exemptions}
          before={totals.before}
          after={onAmountStep ? totals.after : totals.before}
        />,
      ]}
    >
      <Stack spacing={3}>
        {!editing && (
          <Stepper activeStep={step}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {!onAmountStep && (
          <ChooseManifestStep
            candidates={candidates}
            loading={loading}
            search={search}
            onSearch={setSearch}
            selected={selected}
            onSelect={setSelected}
            startDate={startDate}
            manifestsOnEvent={manifestsOnEvent}
            showCodes={exemptions.filedWithEpa}
          />
        )}
        {onAmountStep && (
          <AmountStep
            reliefApplies={reliefApplies}
            candidate={candidate}
            loading={loading}
            showCodes={exemptions.filedWithEpa}
            eventLink={eventLink}
            drafts={drafts}
            onChange={setDrafts}
          />
        )}
      </Stack>
    </Dialog>
  );
}
